import { useMemo, ComponentProps, ReactNode } from 'react';

import { ProductIds } from '@atlassian/ipm-choreographer';
import { envType } from '@atlassiansox/analytics-web-client';
import { EngagementProvider as BaseEngagementProvider } from '@atlassiansox/engagekit-ts';

import { useAppData } from 'src/app/data';
import locale from 'src/locale';
import { analyticsClient } from 'src/utils/analytics/client';
import { BbEnv, getBbEnv } from 'src/utils/bb-env';
import { IS_SERVER } from 'src/utils/ssr';

const getAnalyticsEnvironment = (): envType => {
  const env = getBbEnv();
  if (env === BbEnv.Production) {
    return envType.PROD;
  } else if (env === BbEnv.Staging) {
    return envType.STAGING;
  } else {
    return envType.DEV;
  }
};

type Config = ComponentProps<typeof BaseEngagementProvider>['config'];
const getEngagementProviderConfig = (
  cloudId: string,
  currentUserAccountId: string | undefined | null,
  workspaceId: string | undefined
): Config => {
  return {
    cloudId,
    aaid: currentUserAccountId || '',
    workspaceId: workspaceId || '',
    locale: locale.locale,
    product: 'bitbucket',
    stargateUrl: '/gateway/api',
  };
};

export const EngagementProvider = ({ children }: { children: ReactNode }) => {
  const appData = useAppData();

  // We don't have cloudIds, we use the environment instead.
  const cloudId = getBbEnv();
  const currentUserAccountId = appData.user?.aaid;
  const workspaceId = appData.tenantId;

  const config: Config = useMemo(
    () =>
      getEngagementProviderConfig(cloudId, currentUserAccountId, workspaceId),
    [cloudId, currentUserAccountId, workspaceId]
  );

  const analyticsEnv = getAnalyticsEnvironment();

  const coordinationOptions = useMemo(
    () => ({
      enableChoreographer: true,
      envType: analyticsEnv,
      additionalData: { sourceProductId: ProductIds.BITBUCKET },
    }),
    [analyticsEnv]
  );

  if (IS_SERVER || !currentUserAccountId) {
    return <>{children}</>;
  }

  return (
    <BaseEngagementProvider
      config={config}
      analyticsClientInstance={analyticsClient()}
      coordinationOptions={coordinationOptions}
    >
      <>{children}</>
    </BaseEngagementProvider>
  );
};
